const CMS_REST_API = 'https://ralsnet.jp/wp-json/ralsnet/formatline/v1';
// const CMS_REST_API = 'https://dev01.ralsnet.jp/wp-json/ralsnet/formatline/v1';
// const CMS_REST_API = 'http://localhost:56565/wp-json/ralsnet/formatline/v1';

export type Response<T> = {
  count: number;
  pages: number;
  body: T[];
};

const request = async <T>(path: string, query?: URLSearchParams, abort?: AbortSignal): Promise<Response<T>> => {
  const res = await fetch(`${CMS_REST_API}${path}?${query ? query.toString() : ''}`, { signal: abort });
  if (!res.ok) {
    throw new Error(`fetch error: '${CMS_REST_API}${path}'`);
  }

  const body = (await res.json()) as T[];

  let s = res.headers.get('X-WP-Total');
  const count = s ? parseInt(s, 10) : body.length;
  s = res.headers.get('X-WP-TotalPages');
  const pages = s ? parseInt(s, 10) : 1;

  return {
    count,
    pages,
    body,
  };
};

export const taxonomies = {
  industry: 'works_industry',
  option: 'works_formatline_option',
  taste: 'works_formatline_taste',
} as const;

export const slugs = {
  option: {
    topOriginal: 'top-original',
    fvCustom: 'first-view-custom',
    fvMultipleImage: 'multiple-first-view-image',
    sns: 'sns',
  },
};

export type TaxonomyKey = keyof typeof taxonomies;

export type Term = {
  id: number;
  name: string;
  slug: string;
};

export type Terms = Record<TaxonomyKey, Term[]>;

export type Work = {
  id: number;
  title: string;
  meta: {
    siteName: string;
    purpose: string;
    otherLink: string;
    otherLinkMobile: string;
    comment: string;
    imageUrl?: string;
    imageUrlPickup?: string;
  };
  terms: Record<
    string,
    {
      name: string;
      slug: string;
    }[]
  >;
};

export const createEmptyTerms = (): Terms => {
  const keys = Object.keys(taxonomies) as TaxonomyKey[];
  return keys.reduce((prev, cur) => ({ ...prev, [cur]: [] }), {} as Terms);
};

export const fetchTerms = async (taxonomy: string, abort?: AbortSignal): Promise<Term[]> => {
  const params = new URLSearchParams();
  let page = 1;
  let pages = 0;
  const ret: Term[] = [];
  do {
    params.append('page', page.toString());
    const res = await request<Term>(`/taxonomy/${taxonomy}`, undefined, abort);
    ret.push(...res.body);
    pages = res.pages;
    page += 1;
  } while (page <= pages);

  return ret;
};

export type WorksSearchParameters = {
  page: number;
  limit: number;
  plan: number;
  taste: number[];
  option: number[];
  industry: number[];
};

export const fetchWorks = (search: WorksSearchParameters, abort?: AbortSignal): Promise<Response<Work>> => {
  const params = new URLSearchParams();

  params.append('page', search.page.toString());
  params.append('per_page', Math.min(Math.max(search.limit, 1), 100).toString());

  let i = 0;

  if (search.industry.length) {
    params.append(`tax_query[${i}][taxonomy]`, taxonomies.industry);
    search.industry.forEach((value) => {
      params.append(`tax_query[${i}][terms][]`, value.toString());
    });
    i += 1;
  }

  if (search.taste.length) {
    params.append(`tax_query[${i}][taxonomy]`, taxonomies.taste);
    search.taste.forEach((value) => {
      params.append(`tax_query[${i}][terms][]`, value.toString());
    });
    i += 1;
  }

  if (search.option.length) {
    params.append(`tax_query[${i}][taxonomy]`, taxonomies.option);
    search.option.forEach((value) => {
      params.append(`tax_query[${i}][terms][]`, value.toString());
    });
    i += 1;
  }

  return request<Work>('/works', params, abort);
};

export const fetchPickupWorks = (abort?: AbortSignal): Promise<Response<Work>> => {
  const params = new URLSearchParams();

  params.append('per_page', '5');
  params.append('tax_query[0][taxonomy]', taxonomies.option);
  params.append('tax_query[0][field]', 'slug');
  params.append('tax_query[0][terms][]', slugs.option.topOriginal);
  params.append('tax_query[0][terms][]', slugs.option.fvCustom);

  return request<Work>('/works', params, abort);
};
